import { useNavigate } from 'react-router-dom'

export function TermsOfService() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
            />
          </svg>
          Back
        </button>
        <h1 className="text-3xl font-bold mb-8">Terms of Service</h1>

        <div className="prose prose-gray dark:prose-invert max-w-none space-y-6">
          <section>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Effective Date:{' '}
              {new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>

            <p>
              These Terms of Service ("Terms") govern your use of the Book Manager application
              ("Service", "app", "we", "our", or "us"). By accessing or using our Service, you agree
              to be bound by these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
            <p>By creating an account or using our Service, you confirm that:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>You are at least 13 years of age</li>
              <li>You have the legal capacity to enter into these Terms</li>
              <li>You will comply with all applicable laws and regulations</li>
              <li>You have read and understood our Privacy Policy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">2. Account Registration</h2>
            <p>To use certain features of our Service, you must create an account. You agree to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">3. Use of Service</h2>

            <h3 className="text-xl font-semibold mt-6 mb-3">3.1 Permitted Use</h3>
            <p>You may use our Service to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Manage your personal book library</li>
              <li>Track reading progress and goals</li>
              <li>Add notes and reviews for personal use</li>
              <li>Use the book roulette feature for book selection</li>
              <li>Scan ISBNs to add books to your library</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">3.2 Prohibited Use</h3>
            <p>You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Transmit viruses, malware, or harmful code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Scrape, mine, or harvest data from the Service</li>
              <li>Create multiple accounts for abusive purposes</li>
              <li>Resell or commercially exploit the Service</li>
              <li>Impersonate others or provide false information</li>
              <li>Use automated systems or bots without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">
              4. Content and Intellectual Property
            </h2>

            <h3 className="text-xl font-semibold mt-6 mb-3">4.1 Your Content</h3>
            <p>
              You retain ownership of content you create (book notes, reviews, etc.). By using our
              Service, you grant us a worldwide, non-exclusive, royalty-free license to use, store,
              and display your content solely for providing and improving the Service.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">4.2 Our Content</h3>
            <p>
              The Service, including its design, features, and content (excluding user content), is
              owned by us and protected by intellectual property laws. You may not copy, modify, or
              distribute our content without permission.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">4.3 Book Information</h3>
            <p>
              Book metadata (titles, authors, ISBNs) obtained through our Service is provided for
              personal use only. This information may be subject to third-party copyrights and
              should not be used for commercial purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">5. Privacy and Data Protection</h2>
            <p>
              Your use of our Service is also governed by our Privacy Policy. By using the Service,
              you consent to the collection and use of your information as described in the Privacy
              Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">6. Subscription and Payment Terms</h2>

            <h3 className="text-xl font-semibold mt-6 mb-3">6.1 Free Trial</h3>
            <p>
              New users receive a 14-day free trial to evaluate the Service. No payment information
              is required during the trial period.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">6.2 Subscription Plans</h3>
            <p>After the trial period, continued use requires a paid subscription:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Monthly Plan:</strong> Billed monthly with 30-day billing cycles
              </li>
              <li>
                <strong>Annual Plan:</strong> Billed annually with significant savings
              </li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">6.3 Payment Processing</h3>
            <p>All payments are processed securely through Stripe. By subscribing, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Provide accurate and complete payment information</li>
              <li>Authorize us to charge your payment method</li>
              <li>Keep payment information current and valid</li>
              <li>Accept Stripe's terms of service and privacy policy</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">6.4 Billing and Renewal</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Subscriptions automatically renew unless cancelled</li>
              <li>You will be charged at the beginning of each billing cycle</li>
              <li>Price changes will be communicated 30 days in advance</li>
              <li>No refunds for partial months or unused portions</li>
              <li>Past due accounts may have service suspended</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">6.5 Cancellation and Refunds</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>You may cancel your subscription at any time through the billing portal</li>
              <li>Cancellation takes effect at the end of the current billing period</li>
              <li>You retain access to paid features until the subscription expires</li>
              <li>No refunds for partial billing periods</li>
              <li>We reserve the right to offer discretionary refunds</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">6.6 Billing Exemptions</h3>
            <p>Certain users may receive billing exemptions at our discretion, including:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Beta testers and early adopters</li>
              <li>Business partners and affiliates</li>
              <li>Staff and contractors</li>
              <li>Promotional grants</li>
            </ul>
            <p className="mt-3">
              Exemptions may be temporary or permanent and can be revoked at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">7. Third-Party Services</h2>
            <p>Our Service may integrate with or link to third-party services:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>ISBN lookup services for book information</li>
              <li>Authentication providers (Supabase Auth)</li>
              <li>Payment processing (Stripe)</li>
              <li>Analytics and monitoring services (PostHog, Sentry)</li>
            </ul>
            <p className="mt-3">
              We are not responsible for third-party services' practices or content. Your use of
              such services is governed by their respective terms and policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">8. Disclaimers and Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>MERCHANTABILITY</li>
              <li>FITNESS FOR A PARTICULAR PURPOSE</li>
              <li>NON-INFRINGEMENT</li>
              <li>ACCURACY OR RELIABILITY OF INFORMATION</li>
              <li>UNINTERRUPTED OR ERROR-FREE OPERATION</li>
            </ul>
            <p className="mt-3">
              We do not guarantee that the Service will meet your requirements or be available at
              all times. Use of the Service is at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">9. Limitation of Liability</h2>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES</li>
              <li>LOSS OF PROFITS, DATA, USE, OR GOODWILL</li>
              <li>SERVICE INTERRUPTIONS OR DELAYS</li>
              <li>DAMAGES RESULTING FROM YOUR USE OR INABILITY TO USE THE SERVICE</li>
              <li>DAMAGES FROM UNAUTHORIZED ACCESS TO YOUR DATA</li>
              <li>DAMAGES FROM THIRD-PARTY CONTENT OR SERVICES</li>
            </ul>
            <p className="mt-3">
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE PAST 12 MONTHS, OR
              $100 USD, WHICHEVER IS LESS.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Book Manager and its affiliates,
              officers, directors, employees, and agents from any claims, damages, losses,
              liabilities, costs, and expenses arising from:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Your use or misuse of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your content or activities on the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">11. Termination</h2>
            <p>
              We may suspend or terminate your account and access to the Service at any time, with
              or without cause or notice. You may also delete your account at any time through the
              app settings.
            </p>
            <p className="mt-3">Upon termination:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Your right to use the Service immediately ceases</li>
              <li>We may delete your account and associated data</li>
              <li>Provisions that should survive termination will remain in effect</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material
              changes by posting the updated Terms and changing the "Effective Date" above. Your
              continued use of the Service after changes constitutes acceptance of the modified
              Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">13. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of Brazil,
              without regard to conflict of law principles. Any disputes shall be resolved in the
              courts of Brazil.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">14. Dispute Resolution</h2>
            <p>
              We prefer to resolve disputes informally. Before filing a claim, you agree to try to
              resolve the dispute by contacting us. If we cannot resolve the dispute informally
              within 60 days, either party may initiate formal proceedings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">15. General Provisions</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Entire Agreement:</strong> These Terms constitute the entire agreement
                between you and us
              </li>
              <li>
                <strong>Severability:</strong> If any provision is found invalid, the remaining
                provisions continue in effect
              </li>
              <li>
                <strong>Waiver:</strong> Failure to enforce any right does not constitute a waiver
              </li>
              <li>
                <strong>Assignment:</strong> You may not assign these Terms without our consent
              </li>
              <li>
                <strong>Force Majeure:</strong> We are not liable for delays due to circumstances
                beyond our control
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">16. Contact Information</h2>
            <p>For questions about these Terms of Service, please contact us at:</p>
            <div className="mt-3 pl-6">
              <p>Email: contact@prandogabriel.dev</p>
              <p>Address: Brazil</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
